import { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, ArrowRight, BadgeCheck, Star, Users, Building2, Zap, Flame, Leaf, MapPin, ExternalLink, ArrowDown, ArrowUp, GitCompare, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import IdealProfileQuiz from "@/components/IdealProfileQuiz";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";
import SupplierAvatar from "@/components/SupplierAvatar";

const MAX_COMPARE = 4;

function Stars({ value }) {
    const v = Number(value).toFixed(1);
    return (
        <div className="flex items-center gap-1">
            <Star className="h-3.5 w-3.5 fill-accent text-accent" />
            <span className="font-mono font-bold text-sm">{v}</span>
            <span className="text-xs text-muted-foreground">/5</span>
        </div>
    );
}

function MiniBar({ label, value }) {
    const width = `${(Number(value) / 5) * 100}%`;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono font-bold">{Number(value).toFixed(1)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width }} />
            </div>
        </div>
    );
}

const AudienceIcon = {
    b2c: Users,
    b2b: Building2,
    both: BadgeCheck,
};

const PriceLabel = { 1: "€", 2: "€€", 3: "€€€" };

const SORT_OPTIONS = [
    { value: "overall_rating", cmsKey: "suppliers.sort.overall", defaultLabel: "Note globale", defaultDir: "desc" },
    { value: "price_rating", cmsKey: "suppliers.sort.price_rating", defaultLabel: "Prix (mieux noté)", defaultDir: "desc" },
    { value: "price_tier", cmsKey: "suppliers.sort.price_low", defaultLabel: "Prix (plus économique)", defaultDir: "asc" },
    { value: "green_rating", cmsKey: "suppliers.sort.green", defaultLabel: "Note verte", defaultDir: "desc" },
    { value: "customer_service_rating", cmsKey: "suppliers.sort.service", defaultLabel: "Service client", defaultDir: "desc" },
    { value: "transparency_rating", cmsKey: "suppliers.sort.transparency", defaultLabel: "Transparence", defaultDir: "desc" },
    { value: "digital_rating", cmsKey: "suppliers.sort.digital", defaultLabel: "Digital", defaultDir: "desc" },
    { value: "name", cmsKey: "suppliers.sort.name", defaultLabel: "Nom (A→Z)", defaultDir: "asc" },
    { value: "year_founded", cmsKey: "suppliers.sort.age", defaultLabel: "Ancienneté", defaultDir: "asc" },
];

export default function Suppliers() {
    const c = useSiteContent(keysForMany("common", "suppliers-list"));
    const AudienceLabel = {
        b2c: c("common.audience.b2c_plural", "Particuliers"),
        b2b: c("common.audience.b2b_plural", "Pros"),
        both: c("common.audience.both", "Particuliers · Pros"),
    };
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [sortKey, setSortKey] = useState("overall_rating");
    const [sortDir, setSortDir] = useState("desc");
    const [analysisById, setAnalysisById] = useState({});
    const [analysisLoadingId, setAnalysisLoadingId] = useState(null);
    const [selected, setSelected] = useState([]);

    const toggleSelect = (slug) => {
        setSelected((prev) => {
            if (prev.includes(slug)) return prev.filter((s) => s !== slug);
            if (prev.length >= MAX_COMPARE) {
                toast.error(`${MAX_COMPARE} fournisseurs maximum pour la comparaison`);
                return prev;
            }
            return [...prev, slug];
        });
    };

    const clearSelection = () => setSelected([]);

    const launchCompare = () => {
        if (selected.length < 2) {
            toast.error("Sélectionnez au moins 2 fournisseurs pour comparer");
            return;
        }
        navigate(`/fournisseurs/comparer?ids=${selected.join(",")}`);
    };

    useEffect(() => {
        api.get("/suppliers").then(({ data }) => {
            setSuppliers(data);
        }).catch((e) => {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }).finally(() => setLoading(false));
    }, []);

    const onChangeSortKey = (val) => {
        setSortKey(val);
        const opt = SORT_OPTIONS.find((o) => o.value === val);
        if (opt) setSortDir(opt.defaultDir);
    };

    const filteredSorted = useMemo(() => {
        let list = suppliers;
        if (filter === "green") list = list.filter((s) => s.green_energy);
        else if (filter === "gas") list = list.filter((s) => s.gas);
        else if (filter === "b2b") list = list.filter((s) => s.target_audience === "b2b" || s.target_audience === "both");

        const arr = [...list];
        arr.sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            if (typeof va === "string" && typeof vb === "string") {
                return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
            }
            const na = Number(va) || 0;
            const nb = Number(vb) || 0;
            return sortDir === "asc" ? na - nb : nb - na;
        });
        return arr;
    }, [filter, suppliers, sortKey, sortDir]);

    const requestAnalysis = async (slug) => {
        setAnalysisLoadingId(slug);
        try {
            const { data } = await api.post(`/suppliers/${slug}/ai-analysis`);
            setAnalysisById((m) => ({ ...m, [slug]: data.analysis }));
            toast.success("Analyse générée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setAnalysisLoadingId(null);
        }
    };

    if (loading) {
        return (
            <div className="em-container py-20 text-center text-muted-foreground" data-testid="suppliers-loading">
                <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                <p className="mt-3">Chargement des fournisseurs…</p>
            </div>
        );
    }

    return (
        <div className="em-container py-16" data-testid="suppliers-page">
            <p className="em-eyebrow">{c("suppliers.hero.eyebrow", "Comparatif fournisseurs d'énergie")}</p>
            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-primary mt-2">
                {c("suppliers.hero.title", "Tous les fournisseurs, jugés objectivement.")}
            </h1>
            <p className="text-muted-foreground mt-4 max-w-2xl text-lg">
                {c("suppliers.hero.subtitle", "Notes vérifiées sur 5 critères, services proposés, atouts et limites — pour choisir en connaissance de cause.")}
            </p>

            {/* IDEAL PROFILE QUIZ */}
            <div className="mt-10" data-testid="quiz-section">
                <IdealProfileQuiz />
            </div>

            {/* FILTERS + SORT */}
            <div className="mt-14 flex flex-wrap gap-3 items-center justify-between" data-testid="suppliers-toolbar">
                <div className="flex flex-wrap gap-2 items-center">
                    <Tabs value={filter} onValueChange={setFilter}>
                        <TabsList className="rounded-full">
                            <TabsTrigger value="all" className="rounded-full" data-testid="filter-all">{c("suppliers.filter.all", "Tous")}</TabsTrigger>
                            <TabsTrigger value="green" className="rounded-full gap-2" data-testid="filter-green"><Leaf className="h-4 w-4" />{c("suppliers.filter.green", "Verts")}</TabsTrigger>
                            <TabsTrigger value="gas" className="rounded-full gap-2" data-testid="filter-gas"><Flame className="h-4 w-4" />{c("suppliers.filter.gas", "Gaz")}</TabsTrigger>
                            <TabsTrigger value="b2b" className="rounded-full gap-2" data-testid="filter-b2b"><Building2 className="h-4 w-4" />{c("suppliers.filter.b2b", "Pros")}</TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <span className="text-sm text-muted-foreground ml-2" data-testid="suppliers-count">{filteredSorted.length} fournisseur(s)</span>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs em-eyebrow hidden md:inline">{c("suppliers.sort.label", "Trier par")}</span>
                    <Select value={sortKey} onValueChange={onChangeSortKey}>
                        <SelectTrigger className="rounded-full w-[230px]" data-testid="sort-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SORT_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value} data-testid={`sort-option-${opt.value}`}>
                                    {c(opt.cmsKey, opt.defaultLabel)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                        className="rounded-full"
                        title={sortDir === "asc" ? "Croissant" : "Décroissant"}
                        data-testid="sort-direction-toggle"
                    >
                        {sortDir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                    </Button>
                </div>
            </div>

            {/* SUMMARY TABLE */}
            <div className="mt-6 overflow-x-auto border border-border rounded-3xl bg-card" data-testid="suppliers-table">
                <table className="w-full min-w-[920px]">
                    <thead className="bg-secondary">
                        <tr className="text-left">
                            <th className="px-3 py-4 w-10"></th>
                            <th className="px-5 py-4 em-eyebrow">Fournisseur</th>
                            <th className="px-5 py-4 em-eyebrow">Énergies</th>
                            <th className="px-5 py-4 em-eyebrow">Cible</th>
                            <th className="px-5 py-4 em-eyebrow">Prix</th>
                            <th className="px-5 py-4 em-eyebrow">Note verte</th>
                            <th className="px-5 py-4 em-eyebrow">Service</th>
                            <th className="px-5 py-4 em-eyebrow">Note globale</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredSorted.map((s) => {
                            const Icon = AudienceIcon[s.target_audience] || BadgeCheck;
                            const isSelected = selected.includes(s.slug);
                            return (
                                <tr
                                    key={s.id}
                                    className={`border-t border-border transition-colors ${isSelected ? "bg-accent/10" : "hover:bg-muted/40"}`}
                                    data-testid={`supplier-row-${s.slug}`}
                                >
                                    <td className="px-3 py-4">
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={() => toggleSelect(s.slug)}
                                            data-testid={`compare-checkbox-${s.slug}`}
                                            aria-label={`Sélectionner ${s.name} pour la comparaison`}
                                        />
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <SupplierAvatar slug={s.slug} name={s.name} size="sm" />
                                            <div>
                                                <Link to={`/fournisseurs/${s.slug}`} className="font-display font-bold text-primary hover:underline" data-testid={`supplier-link-${s.slug}`}>
                                                    {s.name}
                                                </Link>
                                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <MapPin className="h-3 w-3" />{s.headquarters}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-2">
                                            {s.electricity && (
                                                <span className="inline-flex items-center gap-1 text-xs bg-secondary px-2 py-1 rounded-full">
                                                    <Zap className="h-3 w-3" />Élec
                                                </span>
                                            )}
                                            {s.gas && (
                                                <span className="inline-flex items-center gap-1 text-xs bg-secondary px-2 py-1 rounded-full">
                                                    <Flame className="h-3 w-3" />Gaz
                                                </span>
                                            )}
                                            {s.green_energy && (
                                                <span className="inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full">
                                                    <Leaf className="h-3 w-3" />Verte
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-1.5 text-sm">
                                            <Icon className="h-4 w-4 text-muted-foreground" />
                                            {AudienceLabel[s.target_audience]}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 font-mono font-bold text-primary">{PriceLabel[s.price_tier]}</td>
                                    <td className="px-5 py-4"><Stars value={s.green_rating} /></td>
                                    <td className="px-5 py-4"><Stars value={s.customer_service_rating} /></td>
                                    <td className="px-5 py-4">
                                        <span className="font-display font-black text-lg text-primary">{Number(s.overall_rating).toFixed(1)}</span>
                                        <span className="text-xs text-muted-foreground">/5</span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* DETAILED CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12" data-testid="suppliers-cards">
                {filteredSorted.map((s) => (
                    <Card
                        key={s.id}
                        className="rounded-3xl border-border em-card-hover bg-card"
                        data-testid={`supplier-card-${s.slug}`}
                    >
                        <CardContent className="p-7">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <SupplierAvatar slug={s.slug} name={s.name} size="md" />
                                    <div>
                                        <Link to={`/fournisseurs/${s.slug}`} className="font-display text-2xl font-bold text-primary hover:underline" data-testid={`supplier-card-link-${s.slug}`}>
                                            {s.name}
                                        </Link>
                                        <p className="text-xs text-muted-foreground">Fondé en {s.year_founded} · {s.headquarters}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant={selected.includes(s.slug) ? "default" : "outline"}
                                        size="sm"
                                        type="button"
                                        onClick={() => toggleSelect(s.slug)}
                                        className={`rounded-full ${selected.includes(s.slug) ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
                                        data-testid={`card-select-${s.slug}`}
                                    >
                                        {selected.includes(s.slug) ? (
                                            <><BadgeCheck className="h-4 w-4 mr-1" />Comparer</>
                                        ) : (
                                            <><GitCompare className="h-4 w-4 mr-1" />Comparer</>
                                        )}
                                    </Button>
                                    {s.green_energy && (
                                        <Badge variant="outline" className="border-primary text-primary hidden md:inline-flex">
                                            <Leaf className="h-3 w-3 mr-1" />100% verte
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            <p className="mt-4 italic text-muted-foreground">"{s.tagline}"</p>
                            <p className="mt-3 text-sm leading-relaxed text-foreground">{s.description}</p>

                            <div className="mt-5 grid grid-cols-2 gap-3">
                                <MiniBar label="Transparence" value={s.transparency_rating} />
                                <MiniBar label="Service client" value={s.customer_service_rating} />
                                <MiniBar label="Note verte" value={s.green_rating} />
                                <MiniBar label="Prix" value={s.price_rating} />
                                <MiniBar label="Digital" value={s.digital_rating} />
                                <div className="flex flex-col items-end justify-end">
                                    <p className="em-eyebrow">Globale</p>
                                    <p className="font-display text-2xl font-black text-primary">{Number(s.overall_rating).toFixed(1)}<span className="text-sm text-muted-foreground">/5</span></p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-5">
                                <div>
                                    <p className="em-eyebrow mb-2">{c("suppliers.card.pros_label", "Atouts")}</p>
                                    <ul className="space-y-1.5">
                                        {s.pros.map((p) => (
                                            <li key={p} className="text-sm flex gap-2"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{p}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <p className="em-eyebrow mb-2">{c("suppliers.card.cons_label", "Limites")}</p>
                                    <ul className="space-y-1.5">
                                        {s.cons.map((p) => (
                                            <li key={p} className="text-sm text-muted-foreground flex gap-2">— {p}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-5 pt-5 border-t border-border">
                                <p className="em-eyebrow mb-1">{c("suppliers.card.best_for_label", "Idéal pour")}</p>
                                <p className="text-sm">{s.best_for}</p>
                            </div>

                            <div className="mt-5 pt-5 border-t border-border">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="em-eyebrow">{c("suppliers.card.analysis_label", "Analyse Energy Market")}</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        type="button"
                                        onClick={() => requestAnalysis(s.slug)}
                                        disabled={analysisLoadingId === s.slug}
                                        data-testid={`analyse-btn-${s.slug}`}
                                        className="rounded-full"
                                    >
                                        {analysisLoadingId === s.slug ? (
                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse…</>
                                        ) : (
                                            <><Sparkles className="h-4 w-4 mr-2" />{c("suppliers.card.analysis_cta", "Générer l'analyse")}</>
                                        )}
                                    </Button>
                                </div>
                                {analysisById[s.slug] && (
                                    <div className="bg-secondary rounded-2xl p-4 text-sm whitespace-pre-line leading-relaxed" data-testid={`analyse-content-${s.slug}`}>
                                        {analysisById[s.slug]}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-2 mt-6 pt-5 border-t border-border">
                                <Button asChild size="sm" className="rounded-full bg-primary hover:bg-primary/90" data-testid={`view-detail-btn-${s.slug}`}>
                                    <Link to={`/fournisseurs/${s.slug}`}>{c("common.cta.see_detail", "Voir la fiche complète")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                                </Button>
                                <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`compare-btn-${s.slug}`}>
                                    <Link to="/comparateur">{c("common.cta.see_offers", "Comparer les offres")}</Link>
                                </Button>
                                {s.website && (
                                    <Button asChild variant="ghost" size="sm" className="rounded-full" data-testid={`website-btn-${s.slug}`}>
                                        <a href={s.website} target="_blank" rel="noopener noreferrer">
                                            {c("common.cta.website", "Site officiel")} <ExternalLink className="h-3.5 w-3.5 ml-2" />
                                        </a>
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* CTA */}
            <div className="mt-16 bg-primary text-primary-foreground rounded-3xl p-10 md:p-14 grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-8">
                <div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                        {c("suppliers.cta.title", "Vous hésitez entre plusieurs fournisseurs ?")}
                    </h2>
                    <p className="text-primary-foreground/80 mt-3 max-w-xl">
                        {c("suppliers.cta.body", "Lancez le comparateur Energy Market — nous croisons les offres en temps réel selon votre profil de consommation.")}
                    </p>
                </div>
                <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12 px-8" data-testid="suppliers-bottom-cta">
                    <Link to="/comparateur">{c("suppliers.cta.button", "Lancer le comparateur")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                </Button>
            </div>

            {/* STICKY COMPARE BAR */}
            {selected.length > 0 && (
                <div className="fixed bottom-6 inset-x-4 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 z-40" data-testid="compare-bar">
                    <div className="bg-primary text-primary-foreground rounded-full shadow-2xl px-5 py-3 flex items-center gap-4 md:gap-5 max-w-xl mx-auto">
                        <div className="flex items-center gap-2 shrink-0">
                            <div className="h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-display font-black">
                                {selected.length}
                            </div>
                            <span className="font-display font-bold text-sm hidden md:inline">{c("suppliers.comparebar.selected_label", "sélectionné(s)")}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                            {selected.map((slug) => {
                                const s = suppliers.find((x) => x.slug === slug);
                                if (!s) return null;
                                return (
                                    <button
                                        key={slug}
                                        type="button"
                                        onClick={() => toggleSelect(slug)}
                                        className="inline-flex items-center gap-1 text-xs bg-primary-foreground/15 hover:bg-primary-foreground/25 rounded-full px-2.5 py-1 transition-colors"
                                        data-testid={`chip-${slug}`}
                                    >
                                        {s.name}
                                        <X className="h-3 w-3" />
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={clearSelection}
                                className="text-primary-foreground hover:bg-primary-foreground/10 rounded-full"
                                data-testid="compare-clear"
                            >
                                {c("suppliers.comparebar.clear", "Effacer")}
                            </Button>
                            <Button
                                onClick={launchCompare}
                                type="button"
                                disabled={selected.length < 2}
                                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                                data-testid="compare-launch"
                            >
                                {c("suppliers.comparebar.launch", "Comparer")} <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
